"use client";
import React, { useEffect, useRef, useState } from "react";
import "./styles/App.scss";

import { SearchBox } from "@mapbox/search-js-react";
import { useParams } from "react-router";
import { useLocation, useNavigate } from "react-router-dom";
import Banner from "./Banner";
import { config } from "./config";
import Header from "./Header";
import Map from "./Map";
import Organize from "./Organize";
import Result from "./Result2";
import { AddressOption, NameSearchResult, NewAPIResponse } from "./ResultTypes";
import {
  findByName,
  findPropertyManager,
  getAddresses,
  getPropertyById,
  getSession,
} from "./services";
import { ReactComponent as HOWITWORKS } from "./svg/HOWITWORKSv2.svg";
// // requestAnim shim layer by Paul Irish
// (window as any).requestAnimFrame = (function () {
//   return (
//     window.requestAnimationFrame ||
//     (window as any).webkitRequestAnimationFrame ||
//     (window as any).mozRequestAnimationFrame ||
//     (window as any).oRequestAnimationFrame ||
//     (window as any).msRequestAnimationFrame ||
//     function (/* function */ callback: any, /* DOMElement */ element: any) {
//       window.setTimeout(callback, 1000 / 60);
//     }
//   );
// })();

// function draw(canvas: any) {
//   if (!canvas.getContext) {
//     return;
//   }
//   const ctx = canvas.getContext("2d");

//   // set line stroke and line width
//   ctx.strokeStyle = "#d3ff00";
//   ctx.lineWidth = 5;

//   // draw a red line
//   ctx.beginPath();
//   ctx.moveTo(100, 100);
//   ctx.lineTo(300, 100);
//   ctx.stroke();
// }

function App({ organize }: { organize?: boolean }) {
  // Usage
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    getSession()
      .then((res) => res.json())
      .then((data) => setSessionId(data.sessionId));
  }, []);

  const { propertyId, addressSearch, query, propertyManager } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [address, setAddress] = React.useState("");
  const [landlord, setLandlord] = React.useState("");
  const [addressOptions, setAddressOptions] = React.useState(
    [] as AddressOption[],
  );
  const [result, setResult] = React.useState(null as NewAPIResponse | null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [focus, setFocus] = React.useState("");
  const [nameNotFound, setNameNotFound] = React.useState(false);
  const [stopSearch, setStopSearch] = React.useState(false);
  const [showLandlordSearchError, setShowLandlordSearchError] =
    React.useState(false);
  const [autocomplete, setAutocomplete] = useState<any>(null);
  const ref = useRef(null);
  const [noResult, showNoResult] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (stopSearch) {
      setStopSearch(false);
      return;
    }
    let title = location.pathname.split("/")[1]?.replace("/", "");
    if (title === "") {
      title = "landing";
      setResult(null);
    }
    console.log("title: " + title);

    if (propertyId) {
      getProperty(propertyId);
    } else if (addressSearch) {
      searchAddress(addressSearch);
    } else if (query) {
      searchLandlord(query);
    } else if (propertyManager) {
      searchLandlord(propertyManager);
    } else {
      setResult(null);
    }
  }, [location, addressSearch, propertyManager, query]);

  useEffect(() => {
    if (organize) {
      setResult(null);
    }
  }, [organize]);

  useEffect(() => {
    if (organize) {
      setResult(null);
    }
  }, [organize]);

  // const canvas = useRef(null);
  // window.addEventListener("resize", resizeCanvas, false);

  // function resizeCanvas() {
  //   if (!canvas || !canvas.current) {
  //     return;
  //   }
  //   console.log(canvas.current);
  //   const c: any = canvas.current;
  //   console.log(c.parentNode);
  //   c.width = c.parentNode.offsetWidth;
  //   c.height = c.parentNode.offsetHeight;
  //   draw(canvas.current);
  // }

  function getProperty(propertyId: string) {
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    getPropertyById(propertyId).then((result) => {
      setIsLoading(false);
      if (result instanceof Array && result.length === 0) {
        showNoResult(true);
      }
      setResult(result);
      setAddress("");
      setLandlord("");
      // const newPath = `/property/${propertyId}`;
    });
  }

  function searchAddress(
    address_1: string,
    city?: string,
    zip_code?: string,
    coordinates?: number[],
  ) {
    setShowLandlordSearchError(false);
    if (isLoading) return;
    setIsLoading(true);
    getAddresses(address_1, city, zip_code, coordinates).then((response) => {
      setIsLoading(false);

      if (response instanceof Array && response.length === 0) {
        showNoResult(true);
        return;
      }

      if ("id" in response) {
        // const { id, result } = response;
        setResult(response);
        setAddress("");
        setLandlord("");
        const newPath = `/property/${response.id}`;
        if (location.pathname !== newPath && !stopSearch) {
          console.log("navigate called");
          navigate(newPath);
        }
        setStopSearch(true);
      } else if (response.length) {
        setAddressOptions(response);
      } else {
        // TODO DISPLAY ERROR THAT NO ADDRESSES WERE FOUND
      }
    });
  }

  function searchLandlord(query: string) {
    setShowLandlordSearchError(false);
    if (isLoading) return;
    setIsLoading(true);
    findByName(query || landlord).then((result: NameSearchResult) => {
      setIsLoading(false);
      let foundButIncludesPropertyManagment = false;
      if (result.type === "no-landlord-name-found") {
        setShowLandlordSearchError(true);
      } else {
        foundButIncludesPropertyManagment = (
          result as any
        ).data.business_owners[0].business_name.includes("PROPERTY MANAGEMENT");
      }

      if (
        result.type === "landlord-name-found" &&
        !foundButIncludesPropertyManagment
      ) {
        setNameNotFound(false);
        setResult(result as any);
        setAddress("");
        setLandlord("");
        const newPath = `/search/${query || landlord}`;
        if (location.pathname !== newPath && !stopSearch) {
          navigate(newPath);
        }
        setStopSearch(true);
      } else if (
        result.type === "no-landlord-name-found" ||
        foundButIncludesPropertyManagment
      ) {
        const name = query || landlord;
        findPropertyManager(name).then((result: any) => {
          setIsLoading(false);
          if (result.type === "property-manager-found") {
            setNameNotFound(false);
            setResult(result as any);
            const newPath = `/property-manager/${name}`;
            if (location.pathname !== newPath && !stopSearch) {
              navigate(newPath);
            }
            setStopSearch(true);
          } else {
            setNameNotFound(true);
          }
        });
      }
    });
    // } else if (searchType === 'propertyManager') {
    //   findPropertyManager(propertyManager!)
    //     .then((result: any) => {
    //       setIsLoading(false)
    //       if (result.type === 'property-manager-found') {
    //         setNameNotFound(false)
    //         setResult(result as any)
    //         const newPath = `/property-manager/${result.name}`;
    //         if (location.pathname !== newPath && !stopSearch) {
    //           navigate(newPath)
    //         }
    //         setStopSearch(true)
    //       }
    //     })
    // }
  }

  function closeResult() {
    navigate(`/`, { replace: true });
    setResult(null);
  }

  function search_selected(addy: string) {
    setAddressOptions([]);
    setAddress(addy);
    searchAddress(addy);
  }

  function scroll() {
    document.querySelector(".body")?.scrollTo({
      top: 300,
      behavior: "smooth",
    });
  }

  return (
    <div>
      {location.pathname === "/" && <Banner></Banner>}
      <Header />

      {
        <div
          className="body"
          style={{ display: !result && !organize ? "block" : "none" }}
        >
          {isLoading ? (
            <div className="fade">
              <div className="loading">Fetching Results</div>
              <div className="spinner-box">
                <div className="leo-border-1">
                  <div className="leo-core-1"></div>
                </div>
                <div className="leo-border-2">
                  <div className="leo-core-2"></div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="main-container" ref={ref}>
            <div className="announcement"> {config.get("announcement")}</div>
            <div className="how-it-works">
              {/* <h3>How it Works</h3> */}
              <HOWITWORKS
                style={{
                  width: "100%",
                  margin: "auto",
                  // background: "white",
                  background: "#d6e967",
                  // border: "1px solid #1a1a1a",
                  borderRadius: "30px",
                  marginBottom: "20px",
                  marginTop: "10px",
                }}
                onClick={() => {
                  // (searchRef as any).current?.scrollIntoView({
                  //   behavior: "smooth",
                  // });
                  scroll();
                }}
              />
              {/* 
              <ol>
                <li>Search your address</li>

                <li>Discover who owns your address</li>
                <li>See what else they own</li>
                <li>Leave a Review</li>
                <li>Connect with your neighbors</li>
              </ol> */}
            </div>
            {/* <div className="caret" /> */}

            <div className="inputs announcement">
              <div className="search-title">
                find your landlord by the name of the building or address they
                own
              </div>
              <div className="input-container">
                <>
                  <SearchBox
                    options={{
                      bbox: config.OREGON_BBOX,
                    }}
                    onChange={() => scroll()}
                    placeholder="search an address"
                    accessToken={config.MAPBOX_TOKEN}
                    onRetrieve={(e) => {
                      console.log(e);
                      const address =
                        e.features[0].properties.context.address?.name;
                      const city = e.features[0].properties.context.place?.name;
                      const zip_code =
                        e.features[0].properties.context.postcode?.name;
                      const coordinates = e.features[0].geometry.coordinates;
                      if (address) {
                        setAddress(address);
                        searchAddress(address, city, zip_code, coordinates);
                      }
                    }}
                  />
                  {/* <AddressAutofill
                  accessToken={MAPBOX_TOKEN}
                    options={{
                      bbox: [-124.55417836738366,41.99161889477894,-116.46390970729706,46.26801803457034]  // USA bbox example
                    }}
                  onRetrieve={e => {
                    console.log(e)
                  }}
                  >
                <input
                  key="address-input" 
                  autoFocus={true}
                  className={focus === 'address' ? 'grow' : undefined}
                  value={address}
                  type='text'
                  onChange={event => {
                    console.log(event)
                    setAddress(
                      event.target.value
                    );
                  }}
                  placeholder="Street Address"
                  onFocus={event => {
                    setSearchType('address')
                    setFocus('address')
                    setLandlord('')
                  }}
                  onKeyDown={handleKeyPress}/>
              </AddressAutofill>
                 */}
                </>
                {/* {address && focus === 'address' && <div className='mobile-search'><button onClick={() => search(searchType, address)}>→</button></div> */}
                {/* } */}
              </div>
              {/* <div className='or'>or</div>
            <div  className="input-container">
              <input
                key="landlord-input"
                className={focus === 'landlord' ? 'grow' : undefined}
                value={landlord}
                type='text'
                onChange={event => {
                  setLandlord(
                    event.target.value
                  );
                }}
                onFocus={event => {
                  setSearchType('landlord')
                  setFocus('landlord')
                  setAddress('')
                }}
                placeholder="Landlord Name"
                onKeyDown={handleKeyPress}></input>{landlord && focus === 'landlord' && <div className='mobile-search'><button onClick={() => search(searchType, landlord)}>→</button></div>}
            </div> */}
            </div>
            {!addressSearch &&
              !query &&
              !result &&
              !organize &&
              !propertyId && <Map />}

            {/* {[0,0,0,0,0,0,0].map((a) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 300" width="200" height="300">
          <rect x="90" y="150" width="20" height="150" fill="#4c3923" />
          <g transform="scale(2,2) translate(-50, -50)">

            <polygon points="100,100 80,130 120,130" fill="#0f5b23" />
            <polygon points="100,120 70,150 130,150" fill="#0f5b23" />
            <polygon points="100,80 85,110 115,110" fill="#0f5b23" />
            <polygon points="100,90 75,120 125,120" fill="#0f5b23" />
            <polygon points="100,70 90,95 110,95" fill="#0f5b23" />
            <polygon points="100,60 95,80 105,80" fill="#0f5b23" />
          </g>
        </svg>

        ))} */}
            {addressOptions.length > 0 && !result ? (
              <div className="options-container">
                <div>Did you mean any of these?</div>
                {addressOptions.map((address, i) => (
                  <div className="option">
                    <button
                      key={i}
                      className="address-options"
                      onClick={() => search_selected(address.address_full)}
                    >
                      {address.address_full
                        .split("APT")[0]
                        .trim()
                        .split("APARTMENT")[0]
                        .trim()
                        .split("UNIT")[0]
                        .trim()
                        .split("STE")[0]
                        .trim()
                        .split("#")[0]
                        .trim()}
                    </button>
                  </div>
                ))}
                <div className="option">
                  <button
                    className="address-options none-of-these"
                    onClick={() => setAddressOptions([])}
                  >
                    None of These
                  </button>
                </div>
              </div>
            ) : (
              <div></div>
            )}

            {noResult && (
              <div className="options-container">
                <div>No Results. May be outside of our search range</div>
                <div className="option">
                  <button
                    className="address-options none-of-these"
                    onClick={() => showNoResult(false)}
                  >
                    Ok
                  </button>
                </div>
              </div>
            )}

            {showLandlordSearchError && (
              <div className="landlord-search-error-wrapper">
                <div className="landlord-search-error">
                  <div>Couldn't find any landlords by that name.</div>
                  <br />
                  <div>
                    It's possible you're searching the property management
                    company, which we do not track
                  </div>
                </div>
              </div>
            )}

            {/* <div className="caret-container">
              <div className="caret"></div>
            </div> */}
          </div>

          {/* <Articles></Articles> */}

          {/* <LandlordGrid/> */}
          <div className="buffer" />
        </div>
      }
      {result && <Result apiResponse={result} closeResult={closeResult} />}

      {organize && <Organize></Organize>}

      {/* <Footer/> */}
    </div>
  );
}

export default App;
